<template>
  <modal
    size="lg"
    ref="modalComponent"
    :show="show"
    @submit="onSubmit"
    @hidden="$emit('input', false)"
    :submit-button-text="submitBtnText"
    :is-submitting="isSubmitting"
    :is-submit-disabled="selectedItems.length === 0"
  >
    <div slot="modal-title" class="mr-1">
      <div class="d-flex align-items-center">
        <i class="stars-icon mr-1"></i>
        Create Initiatives with AI
      </div>
    </div>
    <initiative-hierarchy :kr="kr">
      <link-button
        class="d-flex align-items-center text-medium-1 p-0"
        v-if="isLoading"
        @click="stopGenerating"
        underline
        >Stop generating
        <spinner class="ml-2" size="xs" />
      </link-button>
      <link-button class="text-medium-1 p-0" @click="regenerate" :underline="true" v-else
        >Regenerate
      </link-button>
    </initiative-hierarchy>
    <div class="initiatives-wrapper mb-4">
      <div v-if="loadingState === LoadingState.LOADING">
        <div class="shine-wrapper">
          <div class="shine"></div>
        </div>
        <div class="shine-wrapper">
          <div class="shine"></div>
        </div>
        <div class="shine-wrapper">
          <div class="shine"></div>
        </div>
        <div class="shine-wrapper">
          <div class="shine"></div>
        </div>
      </div>
      <div
        class="text-error pl-2 d-flex align-items-center mt-3"
        v-else-if="loadingState === LoadingState.ERRORED"
      >
        Something went wrong. It happens.
        <link-button class="text-error py-0" @click="regenerate" :underline="true"
          >Try again
        </link-button>
      </div>
      <template v-else>
        <a-i-initiative-row
          v-for="initiative in initiatives"
          :key="initiative.title"
          :title="initiative.title"
          :active="selectedItems.includes(initiative.title)"
          @click="onSelectionChange(initiative.title)"
        />
      </template>
    </div>
  </modal>
</template>

<script>
import LinkButton from '@/components/buttons/LinkButton.vue'
import AIInitiativeRow from '@/components/initiative/aiInitiatives/AIInitiativeRow.vue'
import InitiativeHierarchy from '@/components/initiative/aiInitiatives/InitiativeHierarchy.vue'
import Modal from '@/components/Modal.vue'
import Spinner from '@/components/Spinner.vue'
import { LoadingState } from '@/constants/common'
import { TOAST_TYPE } from '@/constants/toasts'
import service from '@/service'
import urlManager from '@/urlManager'
import getNewInitiativePayload from '@/utils/initiative/getNewInitiativePayload'
import { completeJSON } from '@/utils/jsonCompleter'
import safeJSONParse from '@/utils/safeJSONParse'
import showAPIError from '@/utils/showAPIError'
import showToast from '@/utils/showToast'
import * as R from 'ramda'
import { mapActions } from 'vuex'

/**
 * This shows AI generated initiatives in a Modal
 */
export default {
  name: 'AIInitiativesModal',
  props: ['show', 'kr'],
  model: {
    prop: 'show'
  },
  components: { InitiativeHierarchy, Spinner, LinkButton, AIInitiativeRow, Modal },
  data() {
    return {
      loadingState: LoadingState.LOADING,
      LoadingState,
      tokens: '',
      controller: null,
      backupInitiatives: [],
      selectedItems: [],
      isSubmitting: false
    }
  },
  computed: {
    submitBtnText() {
      if (this.selectedItems.length === 0) {
        return 'Select Initiatives to copy'
      } else if (this.selectedItems.length === 1) {
        return 'Copy 1 Initiatives'
      } else {
        return `Copy ${this.selectedItems.length} Initiatives`
      }
    },
    initiatives() {
      try {
        if (!this.tokens) return []
        const getInitiatives = payload => {
          const initiatives = Array.isArray(payload) ? payload : payload.initiatives
          return initiatives.map(initiative =>
            typeof initiative === 'string' ? { title: initiative } : initiative
          )
        }

        const removeEmptyObjects = initiatives =>
          Array.isArray(initiatives) ? initiatives.filter(i => i.title) : []
        const processInitiativesResponse = R.compose(
          removeEmptyObjects,
          getInitiatives,
          safeJSONParse,
          completeJSON
        )
        return processInitiativesResponse(this.tokens)
      } catch (e) {
        return this.backupInitiatives
      }
    },
    isLoading() {
      return [LoadingState.STREAMING, LoadingState.LOADING].includes(this.loadingState)
    }
  },
  methods: {
    ...mapActions(['createInitiative']),
    stopGenerating() {
      this.cancelStream()
      this.loadingState = LoadingState.LOADED
    },
    regenerate() {
      this.selectedItems = []
      this.triggerRequest()
    },
    onSelectionChange(title) {
      const isPresent = this.selectedItems.includes(title)
      if (isPresent) {
        this.selectedItems = this.selectedItems.filter(item => item !== title)
      } else {
        this.selectedItems.push(title)
      }
    },
    cancelStream() {
      this.controller?.abort()
    },
    triggerRequest() {
      this.cancelStream() // this would cancel previous call if that is under progress
      this.tokens = ''
      this.backupInitiatives = []
      this.loadingState = LoadingState.LOADING
      this.controller = new AbortController()
      const request = service.client({
        method: 'GET',
        onDownloadProgress: response => {
          const target = response.event.target
          this.tokens = target.response
          this.loadingState = LoadingState.STREAMING
        },
        url: urlManager.generateInitiatives(this.kr.organizationId, this.kr.id),
        responseType: 'stream',
        signal: this.controller.signal
      })
      this.request = request
      request
        .then(() => (this.loadingState = LoadingState.LOADED))
        .catch(payload => {
          if (!payload.config?.signal?.aborted) this.loadingState = LoadingState.ERRORED
        })
    },
    async generateInitiatives() {
      try {
        this.triggerRequest()
      } catch (e) {
        showAPIError(e)
      }
    },
    async onSubmit() {
      const requests = this.selectedItems.map(title => {
        const payload = getNewInitiativePayload(this.kr, title, this.kr.organizationId)
        return this.createInitiative(payload)
      })
      this.isSubmitting = true
      Promise.allSettled(requests).then(responses => {
        this.isSubmitting = false
        const isErrored = responses.every(result => result.status === 'rejected')
        if (isErrored) {
          showToast('Something went wrong. please try again', TOAST_TYPE.ERROR)
        } else {
          showToast('Initiatives created successfully')
          this.$emit('initiatives-created')
          this.$refs.modalComponent.hide()
        }
      })
    }
  },
  mounted() {
    this.generateInitiatives()
  },
  watch: {
    initiatives(initiatives) {
      if (initiatives) {
        this.backupInitiatives = initiatives
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';

.initiatives-wrapper {
  min-height: 300px;
  padding-left: 19px;
}

.shine-wrapper {
  height: 48px;
  padding: 16px 20px 16px 8px;

  .shine {
    @include dimension(100%, 100%);
  }

  &:nth-child(1) {
    width: 200px;
  }

  &:nth-child(2) {
    width: 400px;
  }

  &:nth-child(3) {
    width: 250px;
  }

  &:nth-child(4) {
    width: 350px;
  }
}
</style>
